import type { ValuationStatus } from '@einwert/valuation-api-client'
import { Box, Divider, Stack, Tooltip } from '@mui/material'
import type { AppraiserAssignment as AppraiserAssignmentSchema, RootUser as User } from '@einwert/valuation-api-client'
import { useState } from 'react'
import { generatePath, Link as RouterLink } from 'react-router-dom'
import { DYNAMIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { usePatchPlausibility } from 'ClientPortal/src/api/Queries/plausibility'
import { useDownloadReport, useGetValuation } from 'ClientPortal/src/api/Queries/valuations'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { useTranslation } from 'react-i18next'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import {
    isPlausibilityVisibleToAppraisers,
    type Plausibility,
    type ReadValueCheck,
    type ValueCheckStatus,
} from '@einwert/valuation-api-client'
import { IconButton } from 'ComponentLibrary/src/piazza'
import { TextNavButtonNew, TinyButton, TinyNavButton } from 'ComponentLibrary/src/piazza/buttons'
import {
    AppraiserAssignment,
    ContentChip,
    InfoText,
    isoDateStringToLocalizedMonthYear,
    isoDateStringToLocalFormat,
    tokens,
} from 'ComponentLibrary'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import {
    ApprovalStatusAction,
    HandoverStatusAction,
} from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationPlausibility/HandoverApprovalStatusActions'
import { ApprovalModal } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationPlausibility/ApprovalModal'
import { HandoverModal } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationPlausibility/HandoverModal'
import { InternalApprovalModal } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationPlausibility/InternalApprovalModal'
import {
    ArrowUpRightIcon,
    BlueprintIcon,
    CalendarBlankIcon,
    ChatTextIcon,
    DownloadSimpleIcon,
    HighlighterIcon,
} from '@phosphor-icons/react'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type ValuationSectionProps = {
    hasFinalizedValuation?: boolean
    finalizedValuationId?: string
    valuationDate?: string
    valuationStatus?: ValuationStatus
    assetId?: string
    drafts: string[]
    appraiserAssignment?: AppraiserAssignmentSchema
    appraiserUsers: User[]
    users: User[]
    externalAppraisingEntityName?: string
    observerUUIDs?: string[]
    latestDraftPlausibility?: Plausibility
    showPlausibility: boolean
    isLatestDraftPlausibilityPending?: boolean
    valueCheckForOrder?: ReadValueCheck
    userLinkCopy?: Partial<UserLinkCopy>
}

const getVisibleAnnotationAndReplyCount = (plausibility?: Plausibility) => {
    return (plausibility?.annotations ?? []).reduce((totalCount, annotation) => {
        const annotationCount = !annotation.is_deleted && !annotation.is_read_only ? 1 : 0
        const replyCount = annotation.replies?.filter((reply) => !reply.is_deleted && !reply.is_read_only).length ?? 0

        return totalCount + annotationCount + replyCount
    }, 0)
}

const getValueCheckStatusMessage = (valueCheckStatus: ValueCheckStatus, translate: (key: string) => string): string => {
    switch (valueCheckStatus) {
        case 'finalised':
            return translate('status.valueCheckFinalised')
        case 'in_progress':
            return translate('status.valueCheckInProgress')
        default:
            throw new Error(`Unhandled valueCheckStatus: ${valueCheckStatus}`)
    }
}

const getLocalizedDraftChipLabel = ({
    title,
    fallbackLabel,
    formatDraftLabel,
}: {
    title?: string
    fallbackLabel: string
    formatDraftLabel: (version: string) => string
}) => {
    const draftTitle = title?.split(' - ')[0]?.trim()

    if (!draftTitle) {
        return fallbackLabel
    }

    const draftTitleMatch = draftTitle.match(/^(Entwurf|Draft)\s*(.*)$/i)

    if (!draftTitleMatch) {
        return draftTitle
    }

    const draftVersion = draftTitleMatch[2]?.trim()
    return draftVersion ? formatDraftLabel(draftVersion) : fallbackLabel
}

const getStatusMessage = ({
    hasFinalizedValuation,
    hasDraftValuation,
    showPlausibility,
    isLatestDraftApprovedState,
    isLatestDraftVisibleToAppraisers,
    appraiserAssignment,
    appraiserUsers,
    externalAppraisingEntityName,
    valueCheckStatus,
    translate,
    userLinkCopy,
}: {
    hasFinalizedValuation?: boolean
    hasDraftValuation: boolean
    showPlausibility: boolean
    isLatestDraftApprovedState: boolean
    isLatestDraftVisibleToAppraisers: boolean
    appraiserAssignment?: AppraiserAssignmentSchema
    appraiserUsers: User[]
    externalAppraisingEntityName?: string
    valueCheckStatus?: ValueCheckStatus
    translate: (key: string) => string
    userLinkCopy?: Partial<UserLinkCopy>
}) => {
    const hasAppraiserAssignment = !!(appraiserAssignment?.main || appraiserAssignment?.secondary || appraiserAssignment?.tertiary)
    const appraiserElement = hasAppraiserAssignment ? (
        <Box component="span" display="inline-flex" sx={{ verticalAlign: 'text-bottom' }}>
            <AppraiserAssignment
                appraiserAssignment={appraiserAssignment}
                users={appraiserUsers}
                size="small"
                externalAppraisingEntityName={externalAppraisingEntityName}
                userLinkCopy={userLinkCopy}
            />
        </Box>
    ) : (
        translate('status.fallbackAppraiser')
    )

    if (valueCheckStatus !== undefined) {
        return (
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {getValueCheckStatusMessage(valueCheckStatus, translate)}
            </Typography>
        )
    }

    if (hasFinalizedValuation) {
        return (
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {translate('status.finalizedReportReady')}
            </Typography>
        )
    }

    if (hasDraftValuation && !showPlausibility) {
        return (
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {translate('status.draftAvailableWithoutPlausibility')}
            </Typography>
        )
    }

    if (hasDraftValuation && isLatestDraftApprovedState) {
        return (
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {appraiserElement}
                {` ${translate('status.approvalReceived')}`}
            </Typography>
        )
    }

    if (hasDraftValuation && isLatestDraftVisibleToAppraisers) {
        return (
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {appraiserElement}
                {` ${translate('status.annotationsReceived')}`}
            </Typography>
        )
    }

    if (hasDraftValuation) {
        return (
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {translate('status.draftAvailable')}
            </Typography>
        )
    }

    return (
        <Typography variant="textS" color={tokens.ColorTextBold}>
            {`${translate('status.noReportsPrefix')} `}
            {appraiserElement}
            {` ${translate('status.noReportsSuffix')}`}
        </Typography>
    )
}

export function ValuationSection({
    hasFinalizedValuation,
    finalizedValuationId,
    valuationDate,
    valuationStatus,
    assetId,
    drafts,
    appraiserAssignment,
    appraiserUsers,
    users,
    externalAppraisingEntityName,
    observerUUIDs,
    latestDraftPlausibility,
    showPlausibility,
    isLatestDraftPlausibilityPending,
    valueCheckForOrder,
    userLinkCopy,
}: ValuationSectionProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.valuationSection' })
    const locale = useCurrentResolvedLocale()
    const { isPlausibilityInternalApprovalEnabled } = useFlags()
    const valueCheckStatus = valueCheckForOrder?.status
    const isValueCheckForOrder = valueCheckStatus !== undefined
    const latestDraftId = drafts.at(-1)
    const { valuation: latestDraftValuation } = useGetValuation({ valuationId: latestDraftId, enabled: !!latestDraftId })
    const { valuation: finalizedValuation } = useGetValuation({
        valuationId: finalizedValuationId,
        enabled: !!hasFinalizedValuation && !!finalizedValuationId,
    })
    const { downloadReport, isDownloadingReport } = useDownloadReport(assetId)
    const { patchPlausibility, isPatchingPlausibility } = usePatchPlausibility({
        valuationId: latestDraftId,
    })
    const [isHandoverOpen, setIsHandoverOpen] = useState(false)
    const [isApprovalOpen, setIsApprovalOpen] = useState(false)
    const [isInternalApprovalOpen, setIsInternalApprovalOpen] = useState(false)
    const [selectedAppraiserId, setSelectedAppraiserId] = useState<number>()

    const hasDraftValuation = drafts.length > 0
    const hasDraftOrFinalValuation = hasDraftValuation || hasFinalizedValuation
    const isFinalizedValueCheck = valueCheckStatus === 'finalised'
    const isLatestDraftApprovedState = hasDraftValuation && !!latestDraftPlausibility?.is_approved
    const isLatestDraftInternallyApprovedState = hasDraftValuation && !!latestDraftPlausibility?.is_internally_approved
    const isLatestDraftVisibleToAppraisers = hasDraftValuation && isPlausibilityVisibleToAppraisers(latestDraftPlausibility)
    const showPlausibilityDetails = showPlausibility && !isValueCheckForOrder && valuationStatus === 'in_review'
    const shouldShowValuationCard = (!isValueCheckForOrder && hasDraftOrFinalValuation) || isFinalizedValueCheck

    const statusMessage = getStatusMessage({
        hasFinalizedValuation,
        hasDraftValuation,
        showPlausibility,
        isLatestDraftApprovedState,
        isLatestDraftVisibleToAppraisers,
        appraiserAssignment,
        appraiserUsers,
        externalAppraisingEntityName,
        valueCheckStatus,
        translate: t,
        userLinkCopy,
    })

    const shouldShowAnnotationsInfo =
        showPlausibility && !isValueCheckForOrder && !hasFinalizedValuation && hasDraftValuation && !isLatestDraftVisibleToAppraisers

    const valuationDateForCard = hasFinalizedValuation ? valuationDate : (latestDraftValuation?.valuation_date ?? valuationDate)
    const valuationMonthAndYear = isoDateStringToLocalizedMonthYear(valuationDateForCard, 'numeric', locale)
    const valuationCardTitle = t('card.reportTitle', { date: valuationMonthAndYear ?? '-' })
    const valueCheckDateForCard = valueCheckForOrder?.valuation_date ?? valueCheckForOrder?.created_on
    const valueCheckMonthAndYear = isoDateStringToLocalizedMonthYear(valueCheckDateForCard, 'numeric', locale)
    const valueCheckCardTitle = t('card.valueCheckTitle', { date: valueCheckMonthAndYear ?? '-' })
    const valuationChipLabel = hasFinalizedValuation
        ? t('card.final')
        : getLocalizedDraftChipLabel({
              title: latestDraftValuation?.title,
              fallbackLabel: t('card.draft', { count: drafts.length }),
              formatDraftLabel: (version) => t('card.draft', { count: version }),
          })

    const valuationPath =
        assetId &&
        (hasFinalizedValuation && finalizedValuationId
            ? `/${generatePath(DYNAMIC_APP_PATHS.valuationDetailsPage, { assetId, valuationId: finalizedValuationId })}`
            : latestDraftId
              ? `/${generatePath(DYNAMIC_APP_PATHS.valuationDetailsPage, { assetId, valuationId: latestDraftId })}`
              : undefined)

    const draftPath =
        assetId && latestDraftId
            ? `/${generatePath(DYNAMIC_APP_PATHS.valuationDetailsPage, { assetId, valuationId: latestDraftId })}`
            : undefined

    const plausibilityPath =
        assetId && latestDraftId
            ? `/${generatePath(DYNAMIC_APP_PATHS.valuationPlausibilityPage, { assetId, valuationId: latestDraftId })}`
            : undefined

    const valueCheckPath =
        assetId && valueCheckForOrder?.uuid
            ? `/${generatePath(DYNAMIC_APP_PATHS.valueCheckDetailsPage, { assetId, valueCheckId: valueCheckForOrder.uuid })}`
            : undefined

    const cardPath = isFinalizedValueCheck ? valueCheckPath : valuationPath
    const cardTitle = isFinalizedValueCheck ? valueCheckCardTitle : valuationCardTitle
    const cardChipLabel = isFinalizedValueCheck ? t('card.final') : valuationChipLabel
    const cardChipColor = isFinalizedValueCheck || hasFinalizedValuation ? 'green' : 'orange'

    const valuationTransmissionDateForCard = hasFinalizedValuation ? finalizedValuation?.updated_on : latestDraftValuation?.created_on
    const formattedCardDate = isFinalizedValueCheck
        ? isoDateStringToLocalFormat(valueCheckDateForCard, false, locale)
        : isoDateStringToLocalFormat(valuationTransmissionDateForCard, false, locale)
    const annotationAndReplyCount = getVisibleAnnotationAndReplyCount(latestDraftPlausibility)
    const hasAnnotationsToHandover = annotationAndReplyCount > 0
    const hasPlausibilityBeenShared = isLatestDraftVisibleToAppraisers
    const participantIds = latestDraftPlausibility?.participant_ids ?? []
    const hasAppraiserInObserversOrParticipants =
        participantIds.some((participantId) => appraiserUsers.some(({ id }) => id === participantId)) ||
        (observerUUIDs?.some((observerUUID) => appraiserUsers.some(({ uuid }) => uuid === observerUUID)) ?? false)
    const handedOverByUser = users.find((user) => user.uuid === latestDraftPlausibility?.handed_over_by)
    const approvedByUser = users.find((user) => user.uuid === latestDraftPlausibility?.approved_by)
    const internallyApprovedByUser = users.find((user) => user.uuid === latestDraftPlausibility?.internally_approved_by)
    const formattedHandoverDate = isoDateStringToLocalFormat(latestDraftPlausibility?.handed_over_on, false, locale)
    const formattedApprovalDate = isoDateStringToLocalFormat(latestDraftPlausibility?.approved_on, false, locale)
    const formattedInternalApprovalDate = isoDateStringToLocalFormat(latestDraftPlausibility?.internally_approved_on, false, locale)
    const handoverTooltip = hasAnnotationsToHandover ? undefined : t('card.noAnnotations')

    const shouldShowFinalizedActions =
        !showPlausibilityDetails && hasFinalizedValuation && !!assetId && !!finalizedValuationId && !!valuationPath
    const shouldShowFinalizedValueCheckActions = isFinalizedValueCheck && !!assetId && !!valueCheckForOrder?.uuid && !!valueCheckPath
    const shouldShowDraftActions = !isValueCheckForOrder && !hasFinalizedValuation && hasDraftValuation && !!draftPath && !!latestDraftId

    const closeHandoverModal = () => {
        setIsHandoverOpen(false)
        setSelectedAppraiserId(undefined)
    }

    const closeApprovalModal = () => {
        setIsApprovalOpen(false)
        setSelectedAppraiserId(undefined)
    }

    const closeInternalApprovalModal = () => {
        setIsInternalApprovalOpen(false)
        setSelectedAppraiserId(undefined)
    }

    const getUpdatedParticipantIds = () => {
        if (hasAppraiserInObserversOrParticipants || selectedAppraiserId === undefined || participantIds.includes(selectedAppraiserId)) {
            return undefined
        }

        return [...participantIds, selectedAppraiserId]
    }

    const setIsHandedOver = (newHandOver: boolean, idsToUpdate?: number[]) => {
        if (!latestDraftId) {
            return
        }

        patchPlausibility([
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_handed_over',
                value: newHandOver,
            },
        ])
    }

    const setIsApproved = (newApproval: boolean, idsToUpdate?: number[]) => {
        if (!latestDraftId) {
            return
        }

        patchPlausibility([
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_approved',
                value: newApproval,
            },
        ])
    }

    const setIsInternallyApproved = (newApproval: boolean, idsToUpdate?: number[]) => {
        if (!latestDraftId) {
            return
        }

        patchPlausibility([
            ...(idsToUpdate
                ? [
                      {
                          op: 'replace' as const,
                          path: '/participant_ids',
                          value: idsToUpdate,
                      },
                  ]
                : []),
            {
                op: 'replace',
                path: '/is_internally_approved',
                value: newApproval,
            },
        ])
    }

    const handleHandoverConfirm = () => {
        setIsHandedOver(true, getUpdatedParticipantIds())
        closeHandoverModal()
    }

    const handleApprovalConfirm = () => {
        setIsApproved(true, getUpdatedParticipantIds())
        closeApprovalModal()
    }

    const handleInternalApprovalConfirm = () => {
        setIsInternallyApproved(true, getUpdatedParticipantIds())
        closeInternalApprovalModal()
    }

    return (
        <Stack
            direction="column"
            padding={tokens.SizingSpacing200}
            borderRadius={tokens.SizingCornerM}
            border={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
            gap={tokens.SizingSpacing300}
        >
            <Stack direction="column" gap={tokens.SizingSpacing100}>
                <Stack direction="column" gap={tokens.SizingSpacing050}>
                    <Headline
                        variant="headline3"
                        title={isValueCheckForOrder ? t('title.valueCheck') : t('title.reportsAndDrafts')}
                        fullWidth
                    />
                    {statusMessage}
                </Stack>
            </Stack>
            {shouldShowValuationCard && (
                <Stack
                    direction="column"
                    borderRadius={tokens.SizingCornerS}
                    border={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
                    overflow="hidden"
                >
                    <Stack
                        direction="row"
                        paddingY={tokens.SizingSpacing100}
                        paddingX={tokens.SizingSpacing150}
                        gap={tokens.SizingSpacing150}
                        bgcolor={tokens.ColorSurfaceAccentRoyalDefault}
                        alignItems="center"
                    >
                        <BlueprintIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
                        <Stack direction="column" flex={1} alignItems="flex-start" gap={tokens.SizingSpacing050} minWidth={0}>
                            {cardPath ? (
                                <Box component={RouterLink} to={cardPath} sx={{ textDecoration: 'none', minWidth: 0 }}>
                                    <Typography variant="headline4" color={tokens.ColorTextLinkDefault} noWrap>
                                        {cardTitle}
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="headline4" color={tokens.ColorTextLinkDefault}>
                                    {cardTitle}
                                </Typography>
                            )}
                            <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                <ContentChip size="small" highContrast color={cardChipColor} label={cardChipLabel} />
                                <Tooltip title={t('card.submittedAt')} placement="top">
                                    <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center" width="fit-content">
                                        <CalendarBlankIcon size={tokens.SizingIconS} color={tokens.ColorIconDefault} weight="bold" />
                                        <Typography
                                            variant="textS"
                                            color={formattedCardDate ? tokens.ColorTextDefault : tokens.ColorTextDisabled}
                                        >
                                            {formattedCardDate ?? t('noDate')}
                                        </Typography>
                                    </Stack>
                                </Tooltip>
                                {showPlausibilityDetails && (
                                    <Tooltip title={t('card.annotationCount', { count: annotationAndReplyCount })} placement="top">
                                        <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center" width="fit-content">
                                            <ChatTextIcon size={tokens.SizingIconS} color={tokens.ColorIconDefault} weight="bold" />
                                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                                {annotationAndReplyCount}
                                            </Typography>
                                        </Stack>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction="row" gap={tokens.SizingSpacing025} alignItems="center">
                            {showPlausibilityDetails && plausibilityPath ? (
                                hasPlausibilityBeenShared ? (
                                    <TinyNavButton
                                        label={t('card.goToPlausibility')}
                                        leftIcon={<HighlighterIcon />}
                                        rightIcon={<ArrowUpRightIcon />}
                                        href={plausibilityPath}
                                    />
                                ) : (
                                    <TextNavButtonNew
                                        label={t('card.goToPlausibility')}
                                        leftIcon={<HighlighterIcon />}
                                        rightIcon={<ArrowUpRightIcon />}
                                        href={plausibilityPath}
                                    />
                                )
                            ) : (
                                (shouldShowFinalizedActions || shouldShowFinalizedValueCheckActions) && (
                                    <>
                                        {shouldShowFinalizedValueCheckActions && valueCheckPath ? (
                                            <>
                                                <TinyNavButton
                                                    label={t('card.goToValueCheck')}
                                                    rightIcon={<ArrowUpRightIcon />}
                                                    href={valueCheckPath}
                                                />
                                                <IconButton
                                                    size="small"
                                                    variant="tertiary"
                                                    icon={<DownloadSimpleIcon />}
                                                    onClick={() => void downloadReport({ valueCheckId: valueCheckForOrder?.uuid })}
                                                    disabled={isDownloadingReport}
                                                    tooltip={t('card.downloadValueCheck')}
                                                />
                                            </>
                                        ) : shouldShowFinalizedActions && valuationPath ? (
                                            <>
                                                <TinyNavButton
                                                    label={t('card.goToValuation')}
                                                    rightIcon={<ArrowUpRightIcon />}
                                                    href={valuationPath}
                                                />
                                                <IconButton
                                                    size="small"
                                                    variant="tertiary"
                                                    icon={<DownloadSimpleIcon />}
                                                    onClick={() => void downloadReport({ valuationId: finalizedValuationId })}
                                                    disabled={isDownloadingReport}
                                                    tooltip={t('card.downloadReport')}
                                                />
                                            </>
                                        ) : null}
                                    </>
                                )
                            )}
                        </Stack>
                    </Stack>

                    {showPlausibilityDetails && (
                        <Stack
                            direction="row"
                            padding={tokens.SizingSpacing150}
                            gap={tokens.SizingSpacing150}
                            borderTop={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
                            borderBottom={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
                        >
                            <Stack direction="column" flex={1} minWidth={0} justifyContent="center">
                                <HandoverStatusAction
                                    isValuationPlausibilityPending={isLatestDraftPlausibilityPending}
                                    isHandedOver={latestDraftPlausibility?.is_handed_over}
                                    isApproved={latestDraftPlausibility?.is_approved}
                                    isInternallyApproved={latestDraftPlausibility?.is_internally_approved}
                                    handedOverByUser={handedOverByUser}
                                    approvedByUser={approvedByUser}
                                    internallyApprovedByUser={internallyApprovedByUser}
                                    formattedHandoverDate={formattedHandoverDate}
                                    formattedApprovalDate={formattedApprovalDate}
                                    formattedInternalApprovalDate={formattedInternalApprovalDate}
                                    handoverTooltip={handoverTooltip}
                                    isHandoverDisabled={!!handoverTooltip}
                                    onOpenHandoverModal={() => {
                                        setSelectedAppraiserId(undefined)
                                        setIsHandoverOpen(true)
                                    }}
                                    chipSize="small"
                                    sharedChipLabel={t('handover.sharedAnnotations')}
                                    sharedByText={(userName) => t('handover.sharedBy', { userName })}
                                    sharedOnText={(date) => t('handover.sharedOn', { date })}
                                    sharedFallbackLabel={t('handover.shared')}
                                    shareButtonLabel={t('handover.shareAnnotations')}
                                    shareDescription={t('handover.shareDescription')}
                                    unknownUserLabel={t('unknownUser')}
                                />
                            </Stack>

                            <Divider orientation="vertical" flexItem sx={{ borderColor: tokens.ColorBorderDefault }} />

                            <Stack direction="column" flex={1} minWidth={0}>
                                {isPlausibilityInternalApprovalEnabled ? (
                                    <Stack direction="column" gap={tokens.SizingSpacing150} alignItems="flex-end">
                                        <ApprovalStatusAction
                                            isValuationPlausibilityPending={isLatestDraftPlausibilityPending}
                                            isApproved={isLatestDraftInternallyApprovedState}
                                            approvedByUser={internallyApprovedByUser}
                                            formattedApprovalDate={formattedInternalApprovalDate}
                                            onOpenApprovalModal={() => {
                                                setSelectedAppraiserId(undefined)
                                                setIsInternalApprovalOpen(true)
                                            }}
                                            align="right"
                                            approvedChipLabel={t('approvals.approvedDraft')}
                                            approvedTooltipLabel={t('approvals.approvedBy')}
                                            openApprovalDescription={t('approvals.approveByFund')}
                                            chipSize="small"
                                            sectionLabel={t('approvals.fundSection')}
                                            approvedOnText={(date) => t('approvals.approvedOn', { date })}
                                            approvedFallbackLabel={t('approvals.approved')}
                                            approveButtonLabel={t('approvals.approveButton')}
                                            unknownUserLabel={t('unknownUser')}
                                        />
                                        <Divider sx={{ width: '100%', borderColor: tokens.ColorBorderDefault }} />
                                        <ApprovalStatusAction
                                            isValuationPlausibilityPending={isLatestDraftPlausibilityPending}
                                            isApproved={latestDraftPlausibility?.is_approved}
                                            approvedByUser={approvedByUser}
                                            formattedApprovalDate={formattedApprovalDate}
                                            onOpenApprovalModal={() => {
                                                setSelectedAppraiserId(undefined)
                                                setIsApprovalOpen(true)
                                            }}
                                            align="right"
                                            approvedChipLabel={t('approvals.approvedDraft')}
                                            approvedTooltipLabel={t('approvals.approvedBy')}
                                            openApprovalDescription={t('approvals.approveDraftAndRequestFinalization')}
                                            chipSize="small"
                                            sectionLabel={t('approvals.kvgSection')}
                                            approvedOnText={(date) => t('approvals.approvedOn', { date })}
                                            approvedFallbackLabel={t('approvals.approved')}
                                            approveButtonLabel={t('approvals.approveButton')}
                                            unknownUserLabel={t('unknownUser')}
                                        />
                                    </Stack>
                                ) : (
                                    <ApprovalStatusAction
                                        isValuationPlausibilityPending={isLatestDraftPlausibilityPending}
                                        isApproved={latestDraftPlausibility?.is_approved}
                                        approvedByUser={approvedByUser}
                                        formattedApprovalDate={formattedApprovalDate}
                                        onOpenApprovalModal={() => {
                                            setSelectedAppraiserId(undefined)
                                            setIsApprovalOpen(true)
                                        }}
                                        align="right"
                                        chipSize="small"
                                        approvedChipLabel={t('approvals.approvedDraft')}
                                        approvedTooltipLabel={t('approvals.approvedBy')}
                                        openApprovalDescription={t('approvals.approveDraftAndRequestFinalization')}
                                        approvedOnText={(date) => t('approvals.approvedOn', { date })}
                                        approvedFallbackLabel={t('approvals.approved')}
                                        approveButtonLabel={t('approvals.approveButton')}
                                        unknownUserLabel={t('unknownUser')}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    )}

                    {shouldShowDraftActions && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            paddingY={tokens.SizingSpacing050}
                            paddingX={tokens.SizingSpacing150}
                            gap={tokens.SizingSpacing150}
                        >
                            <TinyNavButton
                                label={t('card.goToDraft')}
                                leftIcon={<BlueprintIcon />}
                                rightIcon={<ArrowUpRightIcon />}
                                href={draftPath}
                            />
                            <TinyButton
                                leftIcon={<DownloadSimpleIcon />}
                                label={t('card.downloadDraft')}
                                onClick={() => void downloadReport({ valuationId: latestDraftId })}
                                disabled={isDownloadingReport}
                            />
                        </Stack>
                    )}
                </Stack>
            )}
            {showPlausibilityDetails && (
                <>
                    <HandoverModal
                        isHandoverOpen={isHandoverOpen}
                        onClose={closeHandoverModal}
                        onConfirm={handleHandoverConfirm}
                        isPersisting={isPatchingPlausibility}
                        hasAppraiserInObserversOrParticipants={hasAppraiserInObserversOrParticipants}
                        appraiserUsers={appraiserUsers}
                        selectedAppraiserId={selectedAppraiserId}
                        setSelectedAppraiserId={setSelectedAppraiserId}
                        isAppraiserUsersPending={isLatestDraftPlausibilityPending}
                        copy={{
                            title: t('handoverModal.title'),
                            primaryButton: t('handoverModal.primaryButton'),
                            secondaryButton: t('modal.cancel'),
                            imageAlt: t('handoverModal.imageAlt'),
                            makeAnnotationsVisible: t('handoverModal.makeAnnotationsVisible'),
                            requestNewDraft: t('handoverModal.requestNewDraft'),
                            infoText: t('handoverModal.infoText'),
                            appraiserSelection: {
                                title: t('appraiserSelection.title'),
                                description: t('appraiserSelection.description'),
                                loading: t('appraiserSelection.loading'),
                                empty: t('appraiserSelection.empty'),
                                selectLabel: t('appraiserSelection.selectLabel'),
                            },
                        }}
                    />
                    <ApprovalModal
                        isApprovalOpen={isApprovalOpen}
                        onClose={closeApprovalModal}
                        onConfirm={handleApprovalConfirm}
                        hasNewAnnotations={hasAnnotationsToHandover}
                        commentsAreAlreadyVisible={isLatestDraftVisibleToAppraisers}
                        isPersisting={isPatchingPlausibility}
                        hasAppraiserInObserversOrParticipants={hasAppraiserInObserversOrParticipants}
                        appraiserUsers={appraiserUsers}
                        selectedAppraiserId={selectedAppraiserId}
                        setSelectedAppraiserId={setSelectedAppraiserId}
                        isAppraiserUsersPending={isLatestDraftPlausibilityPending}
                        copy={{
                            title: t('approvalModal.title'),
                            primaryButton: t('approvalModal.primaryButton'),
                            secondaryButton: t('modal.cancel'),
                            imageAlt: t('approvalModal.imageAlt'),
                            makeAnnotationsVisible: t('approvalModal.makeAnnotationsVisible'),
                            requestFinalizationWithAnnotations: t('approvalModal.requestFinalizationWithAnnotations'),
                            requestFinalization: t('approvalModal.requestFinalization'),
                            infoText: t('approvalModal.infoText'),
                            confirmationLabel: t('approvalModal.confirmationLabel'),
                            appraiserSelection: {
                                title: t('appraiserSelection.title'),
                                description: t('appraiserSelection.description'),
                                loading: t('appraiserSelection.loading'),
                                empty: t('appraiserSelection.empty'),
                                selectLabel: t('appraiserSelection.selectLabel'),
                            },
                        }}
                    />
                    <InternalApprovalModal
                        isOpen={isInternalApprovalOpen}
                        onClose={closeInternalApprovalModal}
                        onConfirm={handleInternalApprovalConfirm}
                        isPersisting={isPatchingPlausibility}
                        commentsAreAlreadyVisible={isLatestDraftVisibleToAppraisers}
                        hasAppraiserInObserversOrParticipants={hasAppraiserInObserversOrParticipants}
                        appraiserUsers={appraiserUsers}
                        selectedAppraiserId={selectedAppraiserId}
                        setSelectedAppraiserId={setSelectedAppraiserId}
                        isAppraiserUsersPending={isLatestDraftPlausibilityPending}
                        copy={{
                            title: t('internalApprovalModal.title'),
                            primaryButton: t('internalApprovalModal.primaryButton'),
                            secondaryButton: t('modal.cancel'),
                            imageAlt: t('internalApprovalModal.imageAlt'),
                            fundApproval: t('internalApprovalModal.fundApproval'),
                            confirmationLabel: t('internalApprovalModal.confirmationLabel'),
                            appraiserSelection: {
                                title: t('appraiserSelection.title'),
                                description: t('appraiserSelection.description'),
                                loading: t('appraiserSelection.loading'),
                                empty: t('appraiserSelection.empty'),
                                selectLabel: t('appraiserSelection.selectLabel'),
                            },
                        }}
                    />
                </>
            )}
            {shouldShowAnnotationsInfo && <InfoText size="small" variant="information" text={t('annotationsInfo')} />}
        </Stack>
    )
}
